"use strict";

import { Vector3 } from "./vectors.js";

export class Quaternion {
    static lookAtObject(obj1,obj2) {
        const pos1 = obj1.getRealWorldPosition();
        const pos2 = obj2.getRealWorldPosition();
        const direction = Vector3.subtract(pos2, pos1);
        let euler = Quaternion.toEuler(Quaternion.lookAt(direction));
        euler.x = -euler.x;
        euler.z += 180;
        obj1.setRotation(euler);
    }

    static lookAt(forward,up) {
        if (up === undefined) {
            up = Vector3.up();
        }

        Vector3.normalize(forward);
        let right = Vector3.cross(up, forward);
        Vector3.normalize(right);
        up = Vector3.cross(forward, right);

        let m00 = right.x;
        let m01 = right.y;
        let m02 = right.z;
        let m10 = up.x;
        let m11 = up.y;
        let m12 = up.z;
        let m20 = forward.x;
        let m21 = forward.y;
        let m22 = forward.z;
        let num8 = (m00 + m11) + m22;

        let quaternion = { x: 0, y: 0, z: 0, w: 0 };

        if (num8 > 0) {
            let num = Math.sqrt(num8 + 1);
            quaternion.w = num * 0.5;
            num = 0.5 / num;
            quaternion.x = (m12 - m21) * num;
            quaternion.y = (m20 - m02) * num;
            quaternion.z = (m01 - m10) * num;
            return quaternion;
        }

        if ((m00 >= m11) && (m00 >= m22)) {
            let num7 = Math.sqrt(1 + m00 - m11 - m22);
            let num4 = 0.5 / num7;
            quaternion.x = 0.5 * num7;
            quaternion.y = (m01 + m10) * num4;
            quaternion.z = (m02 + m20) * num4;
            quaternion.w = (m12 - m21) * num4;
            return quaternion;
        }

        if (m11 > m22) {
            let num6 = Math.sqrt(1 + m11 - m00 - m22);
            let num3 = 0.5 / num6;
            quaternion.x = (m10 + m01) * num3;
            quaternion.y = 0.5 * num6;
            quaternion.z = (m21 + m12) * num3;
            quaternion.w = (m20 - m02) * num3;
            return quaternion;
        }

        let num5 = Math.sqrt(1 + m22 - m00 - m11);
        let num2 = 0.5 / num5;
        quaternion.x = (m20 + m02) * num2;
        quaternion.y = (m21 + m12) * num2;
        quaternion.z = 0.5 * num5;
        quaternion.w = (m01 - m10) * num2;
        return quaternion;
    }

    static toEuler(quaternion) {
        let sqw = quaternion.w * quaternion.w;
        let sqx = quaternion.x * quaternion.x;
        let sqy = quaternion.y * quaternion.y;
        let sqz = quaternion.z * quaternion.z;
        let unit = sqx + sqy + sqz + sqw;
        let test = quaternion.x * quaternion.w - quaternion.y * quaternion.z;

        let v = { x: 0, y: 0, z: 0 };

        if (test > 0.4995 * unit)
        {
            v.x = Math.PI / 2;
            v.y = 2 * Math.atan2(quaternion.y, quaternion.x);
            Vector3.normalizeAngles(Vector3.radiansToDegrees(v));
            return v;
        }

        if (test < -0.4995 * unit)
        {
            v.x = -Math.PI / 2;
            v.y = -2 * Math.atan2(quaternion.y, quaternion.x);
            v.z = 0;
            Vector3.normalizeAngles(Vector3.radiansToDegrees(v));
            return v;
        }

        let q = { x: quaternion.w, y: quaternion.z, z: quaternion.x, w: quaternion.y };

        v.x = Math.asin(2 * (q.x * q.z - q.w * q.y));
        v.y = Math.atan2(2 * q.x * q.w + 2 * q.y * q.z, 1 - 2 * (q.z * q.z + q.w * q.w));
        v.z = Math.atan2(2 * q.x * q.y + 2 * q.z * q.w, 1 - 2 * (q.y * q.y + q.z * q.z));

        Vector3.normalizeAngles(Vector3.radiansToDegrees(v));
        return v;
    }
}